<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
    <div class="orangehrm-background-container">
        <div class="orangehrm-paper-container">
            <div class="orangehrm-header-container">
                <oxd-text tag="h6" class="orangehrm-main-title">
                    {{ $t('admin.subscribers') }}: {{ title }}
                </oxd-text>
                <div>
                    <oxd-button
                        :label="$t('general.add')"
                        icon-name="plus"
                        display-type="secondary"
                        @click="onClickAdd"
                    />
                </div>
            </div>
            <table-header
                :selected="checkedItems.length"
                :total="total"
                :loading="isLoading"
                @delete="onClickDeleteSelected"
            ></table-header>
            <div class="orangehrm-container">
                <oxd-card-table
                    v-model:selected="checkedItems"
                    :loading="isLoading"
                    :headers="headers"
                    :items="items?.data"
                    :selectable="true"
                    :clickable="false"
                    row-decorator="oxd-table-decorator-card"
                />
            </div>
            <div class="orangehrm-bottom-container">
                <oxd-pagination
                    v-if="showPaginator"
                    v-model:current="currentPage"
                    :length="pages"
                />
            </div>
        </div>

        <delete-confirmation ref="deleteDialog"></delete-confirmation>
        <save-subscriber
            v-if="showSaveModal"
            :data="{subscriptionId}"
            @close="onSaveModalClose"
        ></save-subscriber>
        <edit-subscriber
            v-if="showEditModal"
            :data="editModalState"
            @close="onEditModalClose"
        ></edit-subscriber>
    </div>
</template>

<script>
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import {APIService} from '@ohrm/core/util/services/api.service';
import SaveSubscriber from './SaveSubscriber';
import EditSubscriber from './EditSubscriber';

export default {
    components: {
        'save-subscriber': SaveSubscriber,
        'edit-subscriber': EditSubscriber,
        'delete-confirmation': DeleteConfirmationDialog,
    },
    props: {
        subscriptionId: {
            type: String,
            required: true,
        },
    },

    setup(props) {
        const http = new APIService(
            window.appGlobal.baseUrl,
            `/api/v2/admin/email-subscriptions/${props.subscriptionId}/subscribers`,
        );

        const {
            showPaginator,
            currentPage,
            total,
            pages,
            pageSize,
            response,
            isLoading,
            execQuery,
        } = usePaginate(http);

        return {
            http,
            showPaginator,
            currentPage,
            isLoading,
            total,
            pages,
            pageSize,
            execQuery,
            items: response,
        };
    },

    data() {
        return {
            headers: [
                {
                    name: 'name',
                    slot: 'title',
                    title: this.$t('general.name'),
                    style: {flex: '30%'},
                },
                {
                    name: 'email',
                    title: this.$t('general.email'),
                    style: {flex: '55%'},
                },
                {
                    name: 'actions',
                    title: this.$t('general.actions'),
                    slot: 'action',
                    style: {flex: '15%'},
                    cellType: 'oxd-table-cell-actions',
                    cellConfig: {
                        delete: {
                            onClick: this.onClickDelete,
                            component: 'oxd-icon-button',
                            props: {
                                name: 'trash',
                            },
                        },
                        edit: {
                            onClick: this.onClickEdit,
                            props: {
                                name: 'pencil-fill',
                            },
                        },
                    },
                },
            ],
            checkedItems: [],
            showSaveModal: false,
            showEditModal: false,
            editModalState: null,
        };
    },

    computed: {
        title() {
            return this.items.meta ? this.items.meta?.name : '';
        },
    },

    methods: {
        onClickAdd() {
            this.showSaveModal = true;
        },
        onClickEdit(item) {
            this.editModalState = {
                ...item,
                subscriptionId: this.subscriptionId,
            };
            this.showEditModal = true;
        },
        onSaveModalClose() {
            this.showSaveModal = false;
            this.resetDataTable();
        },
        onEditModalClose() {
            this.editModalState = null;
            this.showEditModal = false;
            this.resetDataTable();
        },
        onClickDeleteSelected() {
            const ids = this.checkedItems.map((index) => {
                return this.items?.data[index].id;
            });
            this.$refs.deleteDialog.showDialog().then((confirmation) => {
                if (confirmation === 'ok') {
                    this.deleteItems(ids);
                }
            });
        },
        onClickDelete(item) {
            this.$refs.deleteDialog.showDialog().then((confirmation) => {
                if (confirmation === 'ok') {
                    this.deleteItems([item.id]);
                }
            });
        },
        deleteItems(items) {
            if (items instanceof Array) {
                this.isLoading = true;
                this.http
                    .deleteAll({
                        ids: items,
                    })
                    .then(() => {
                        return this.$toast.deleteSuccess();
                    })
                    .then(() => {
                        this.isLoading = false;
                        this.resetDataTable();
                    });
            }
        },
        async resetDataTable() {
            this.checkedItems = [];
            await this.execQuery();
        },
    },
};
</script>
